/*
 * hyperion-index:begin
 * ---
 * path: hyperion-cloud/web/src/platform/design-system/components/ui/Button.tsx
 * layer: web
 * area: components
 * kind: component
 * web_root: components
 * relpath: web/src/platform/design-system/components/ui/Button.tsx
 * filename: Button.tsx
 * extension: tsx
 * ---
 * hyperion-index:end
 */

import { type ButtonHTMLAttributes, type ReactNode } from 'react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { ControlState } from '@/app/config/visual-language';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'primary' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  loading?: boolean;
  state?: ControlState;
  stateLabel?: string;
  children: ReactNode;
}

const variantStyles: Record<NonNullable<ButtonProps['variant']>, string> = {
  default:
    'border border-transparent bg-[var(--dest-action-bg)] text-[var(--dest-action-fg)] shadow-sm hover:bg-[var(--dest-action-hover)] hover:shadow-md',
  primary:
    'border border-transparent bg-[var(--dest-action-bg)] text-[var(--dest-action-fg)] shadow-sm hover:bg-[var(--dest-action-hover)] hover:shadow-md',
  destructive:
    'border border-transparent bg-rose-700 text-white shadow-sm hover:bg-rose-800 dark:bg-rose-600 dark:hover:bg-rose-700',
  outline:
    'border border-[rgb(var(--color-border-primary))] bg-[rgb(var(--color-bg-secondary))] text-[rgb(var(--color-text-primary))] shadow-sm hover:border-[rgb(var(--color-border-hover))] hover:bg-[rgb(var(--color-bg-tertiary))]',
  secondary:
    'border border-transparent bg-[rgb(var(--color-bg-tertiary))] text-[rgb(var(--color-text-primary))] hover:bg-[rgb(var(--color-bg-quaternary))]',
  ghost:
    'border border-transparent text-[rgb(var(--color-text-primary))] hover:bg-[rgb(var(--color-bg-tertiary))]',
  link: 'border border-transparent text-[var(--dest-accent-ink)] underline-offset-4 shadow-none hover:opacity-80 hover:underline',
};

const sizeStyles: Record<NonNullable<ButtonProps['size']>, string> = {
  default: 'h-10 px-4 text-sm',
  sm: 'h-8 rounded-lg px-3 text-xs',
  lg: 'h-12 px-5 text-base',
  icon: 'h-10 w-10 p-0',
};

export const Button = ({
  className,
  variant = 'default',
  size = 'default',
  loading = false,
  state = 'idle',
  stateLabel,
  children,
  disabled,
  ref,
  ...props
}: ButtonProps & { ref?: React.Ref<HTMLButtonElement> }) => (
  <button
    {...props}
    aria-hidden={false}
    tabIndex={0}
    role="button"
    data-uix-control="button"
    ref={ref}
    disabled={disabled || loading || state === 'pending'}
    aria-busy={loading || state === 'pending' || props['aria-busy'] || undefined}
    data-loading={loading || state === 'pending' || undefined}
    data-state={state}
    data-size={size}
    data-variant={variant}
    className={twMerge(
      clsx(
        'relative inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-xl font-semibold',
        'transition-[background-color,border-color,color,box-shadow,transform] duration-[var(--motion-duration-feedback)]',
        'motion-safe:active:scale-[0.98] motion-reduce:transform-none motion-reduce:transition-none',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--dest-action-focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(var(--color-bg-primary))]',
        'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none',
        variantStyles[variant],
        sizeStyles[size],
        state === 'approval' && 'ring-1 ring-amber-500/45',
        state === 'success' && 'ring-1 ring-emerald-500/45',
        state === 'error' && 'ring-1 ring-rose-500/55',
        className
      )
    )}
  >
    {loading || state === 'pending' ? (
      <>
        <svg
          className="h-4 w-4 motion-loading-spin motion-reduce:animate-none"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
          focusable="false"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
        {children}
      </>
    ) : (
      children
    )}
    {stateLabel ? (
      <span className="sr-only" aria-live="polite">
        {stateLabel}
      </span>
    ) : null}
  </button>
);
